import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  changePassword as changePasswordApi,
  fetchMe,
  login as loginApi,
  logout as logoutApi,
} from "../api/auth";
import type { SessionResponse, UserSummary } from "../api/types";

interface AuthContextValue {
  session?: SessionResponse;
  user?: UserSummary;
  isLoading: boolean;
  isAuthed: boolean;
  login: (payload: { username: string; password: string }) => Promise<void>;
  logoutNow: () => Promise<void>;
  changePassword: (payload: { currentPassword: string; newPassword: string }) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const sessionQuery = useQuery({
    queryKey: ["auth", "me"],
    queryFn: fetchMe,
    retry: false,
    staleTime: 60_000,
  });

  const loginMutation = useMutation({
    mutationFn: loginApi,
    onSuccess: (session) => {
      queryClient.setQueryData(["auth", "me"], session);
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: logoutApi,
    onSuccess: () => {
      queryClient.clear();
    },
  });

  const changePasswordMutation = useMutation({ mutationFn: changePasswordApi });

  const session = sessionQuery.data;

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user,
      isLoading: sessionQuery.isLoading,
      isAuthed: Boolean(session?.user),
      login: async (payload) => {
        await loginMutation.mutateAsync(payload);
      },
      logoutNow: async () => {
        await logoutMutation.mutateAsync();
      },
      changePassword: async (payload) => {
        await changePasswordMutation.mutateAsync(payload);
      },
    }),
    [session, sessionQuery.isLoading, loginMutation, logoutMutation, changePasswordMutation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth wajib dipakai di dalam AuthProvider");
  return context;
}
